import { PlayerServiceAPI } from '@API/data-services/player-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { LoadingRows } from '@Components/util/loading-rows'
import { formatToCurrencyDisplay } from '@Components/util/utils'
import { Box, Container, Flex, Heading, Skeleton, Table } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { formatInTimeZone } from 'date-fns-tz'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store.ts'
import { AdminTicketScanResponse, ScanStatus, TicketType } from '@Generated/back-office-client/src'
import { TicketBarcode } from '@Components/tools/player/ticket-barcode.tsx'
import { omit } from 'lodash'
import { SideDrawer } from '@Components/common/side-drawer'
import { JsonView } from '@Components/common/json-view'
import { Pagination } from '@Components/common/pagination'
import { usePaginatedState } from '@Components/common/use-paginated-state'

const PAGE_SIZE = 20

export const ScanHistory = () => {
  const params = useParams()
  const playerLookupId = params?.lookupId
  const [page, setPage] = usePaginatedState()
  const [showScanDetails, setShowScanDetails] = useState<AdminTicketScanResponse | undefined>(
    undefined,
  )

  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)

  const { data: ticketScanHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: [`${playerLookupId}-scan-history-${page}`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return PlayerServiceAPI.getPlayerTicketScans(
        playerLookupId!,
        page * PAGE_SIZE,
        (page + 1) * PAGE_SIZE,
      )
    },
    enabled: !!playerLookupId,
  })

  const mapTicketScans = useCallback(() => {
    return ticketScanHistory?.ticketScans.map((ticketScan) => {
      return (
        <Table.Row key={ticketScan.scanId}>
          <Table.RowHeaderCell>
            {formatInTimeZone(
              ticketScan.timestamp,
              activeCustomerMetadata?.timezone ?? 'UTC',
              'PP',
            )}
            <br />
            {formatInTimeZone(ticketScan.timestamp, activeCustomerMetadata?.timezone ?? 'UTC', 'p')}
          </Table.RowHeaderCell>
          <Table.Cell>{mapTicketType(ticketScan.ticketType)}</Table.Cell>
          <Table.Cell>{ticketScan.gameName ?? 'N/A'}</Table.Cell>
          <Table.Cell>
            <a className="open-scan-details" onClick={() => setShowScanDetails(ticketScan)}>
              {ticketScan.scanId}
            </a>
          </Table.Cell>
          <Table.Cell>
            {ticketScan.status === ScanStatus.ERROR ? 'Error - ' : ''}
            {ticketScan.details}
          </Table.Cell>
          <Table.Cell>
            <p>{formatToCurrencyDisplay(ticketScan.winAmountInCents)}</p>
            {ticketScan.additionalWinnings ? ticketScan.additionalWinnings : null}
          </Table.Cell>
          <Table.Cell>
            <TicketBarcode ticketScan={ticketScan} />
          </Table.Cell>
        </Table.Row>
      )
    })
  }, [activeCustomerMetadata?.timezone, ticketScanHistory?.ticketScans])

  const mapTicketType = (ticketType: TicketType): string => {
    switch (ticketType) {
      case 'INSTANT':
        return 'Scratch'
      case 'DRAW':
      case 'RAPID':
      case 'FAST_PLAY':
        return 'Terminal'
      default:
        return 'N/A'
    }
  }

  return (
    <Container>
      <Flex direction="column" gap="3">
        <Skeleton loading={isHistoryLoading}>
          {ticketScanHistory?.ticketScans.length || isHistoryLoading ? (
            <Table.Root className="results-table">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Date & Time</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Game Type</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Game Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Transaction ID</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Details</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Winnings</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Barcode Reference</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>{isHistoryLoading ? <LoadingRows /> : mapTicketScans()}</Table.Body>
            </Table.Root>
          ) : (
            <Flex>No scan history for {playerLookupId}</Flex>
          )}
        </Skeleton>
        {ticketScanHistory?.totalResults ? (
          <Pagination
            totalItems={ticketScanHistory.totalResults}
            pageSize={PAGE_SIZE}
            currentPage={page}
            onPageChange={setPage}
          />
        ) : null}
      </Flex>
      {showScanDetails ? (
        <SideDrawer onClose={() => setShowScanDetails(undefined)}>
          <Flex direction="column" gap="5">
            <Flex direction="column" gap="2">
              <Heading as="h3" size="4">
                Gambyt Ticket Scan Response
              </Heading>
              <Box className="scan-details-json">
                <JsonView data={omit(showScanDetails, 'externalScanResponse')} />
              </Box>
            </Flex>

            <Flex direction="column" gap="2">
              <Heading as="h3" size="4">
                External Ticket Scan Response
              </Heading>
              <Box className="scan-details-json">
                <JsonView data={showScanDetails.externalScanResponse ?? {}} />
              </Box>
            </Flex>
          </Flex>
        </SideDrawer>
      ) : null}
    </Container>
  )
}
